import React, { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { createPortal } from 'react-dom';
import { IoMdAdd } from 'react-icons/io';

import TaskCard from './TaskCard';
import KanbanBoardColumn from './KanbanBoardColumn';
import { generatedId } from '../../utils/helper';
import { kanbanBoardCols } from '../../utils/kanbanBoardCols';

const ColumnContainer = () => {
  const [columns, setColumns] = useState(kanbanBoardCols);
  const [activeCol, setActiveCol] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );
  const columnsId = useMemo(() => {
    return columns.map((col) => col.id);
  }, [columns]);
  const handleDragStart = (e) => {
    if (e.active.data.current?.type === 'column') {
      setActiveCol(e.active.data.current?.column);
    }
    if (e.active.data.current?.type === 'task')
      setActiveTask(e.active.data.current?.issue);
  };
  const handleDragEnd = (e) => {
    setActiveCol(null);
    setActiveTask(null);
    const { active, over } = e;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    if (
      active.data.current?.type === 'column' &&
      over.data.current?.type === 'column'
    ) {
      if (activeId === overId) return;

      setColumns((cols) => {
        const activeColIndex = cols.findIndex((col) => col.id === activeId);
        const overColIndex = cols.findIndex((col) => col.id === overId);
        return arrayMove([...cols], activeColIndex, overColIndex);
      });
      return;
    }

    if (
      active.data.current?.type === 'task' &&
      over.data.current?.type === 'task'
    ) {
      const activeColumnId = active.data.current?.issue?.columnId;
      const overColumnId = over.data.current?.issue?.columnId;

      if (activeColumnId === overColumnId) {
        if (activeId === overId) return;
        setColumns((cols) => {
          return cols.map((col) => {
            if (col.id !== activeColumnId) return col;

            const oldIndex = col.issues.findIndex(
              (issue) => issue.id === active.id,
            );
            const newIndex = col.issues.findIndex(
              (issue) => issue.id === over.id,
            );

            if (oldIndex === -1 || newIndex === -1) return col;

            return {
              ...col,
              issues: arrayMove([...col.issues], oldIndex, newIndex),
            };
          });
        });
        return;
      }
      if (activeColumnId !== overColumnId) {
        setColumns((cols) => {
          let taskToMove = null;

          const updatedCols = cols.map((col) => {
            if (col.id === activeColumnId) {
              taskToMove = col.issues.find((issue) => issue.id === activeId);
              return {
                ...col,
                issues: col.issues.filter((issue) => issue.id !== activeId), // Remove from source column
              };
            }
            return col;
          });

          if (!taskToMove) return cols; // Prevent issues if taskToMove is null

          return updatedCols.map((col) => {
            if (col.id === overColumnId) {
              const overIndex = col.issues.findIndex(
                (issue) => issue.id === overId,
              );
              const newIssues = [...col.issues];
              newIssues.splice(overIndex, 0, {
                ...taskToMove,
                columnId: overColumnId,
              }); // Insert at correct position

              return {
                ...col,
                issues: newIssues,
              };
            }
            return col;
          });
        });
      }
    }

    if (active.data.current?.type === 'task') {
      const activeTask = active.data.current.issue;
      const sourceColumnId = activeTask.columnId;
      const targetColumnId = over.data.current?.column?.id || overId;
      if (!targetColumnId) return;

      setColumns((cols) => {
        let taskToMove = null;
        let newColumns = cols.map((col) => {
          if (col.id === sourceColumnId) {
            taskToMove = col.issues.find((i) => i.id === activeTask.id);
            return {
              ...col,
              issues: col.issues.filter((i) => i.id !== activeTask.id),
            };
          }
          return col;
        });

        if (!taskToMove) return cols;

        return newColumns.map((col) => {
          if (col.id === targetColumnId) {
            return {
              ...col,
              issues: [
                ...col.issues,
                { ...taskToMove, columnId: targetColumnId },
              ],
            };
          }
          return col;
        });
      });
    }
  };
  const handleAddColumn = () => {
    setColumns((cols) => {
      return [
        ...cols,
        {
          title: `New column ${cols.length + 1}`,
          id: generatedId(),
          issues: [],
        },
      ];
    });
  };
  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      sensors={sensors}
    >
      <div className="flex max-w-full flex-nowrap gap-4 overflow-x-auto border border-zinc-700 bg-zinc-800 p-2">
        <SortableContext items={columnsId}>
          {columns.map((column) => (
            <KanbanBoardColumn
              key={column.id}
              column={column}
              onUpdateTitle={(newTitle) => {
                setColumns((cols) =>
                  cols.map((col) =>
                    col.id === column.id ? { ...col, title: newTitle } : col,
                  ),
                );
              }}
              onDeleteColumn={() => {
                setColumns((cols) =>
                  cols.filter((col) => col.id !== column.id),
                );
              }}
            />
          ))}
        </SortableContext>
        <div className="relative min-h-[200px] w-[250px] min-w-[200px] rounded-md border border-gray-600 bg-[#1c1c20] p-4">
          <button
            className="absolute left-2 top-2 flex bg-black p-2"
            onClick={handleAddColumn}
          >
            <IoMdAdd className="font-bold text-white" size={20} />
          </button>
        </div>
      </div>
      {createPortal(
        <DragOverlay>
          {activeCol && <KanbanBoardColumn column={activeCol} />}
          {activeTask && <TaskCard issue={activeTask} />}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};

export default ColumnContainer;
